import { useState } from "react";
import { useFile } from "../../../contexts/FileContext";
import { useFolder } from "../../../contexts/FolderContext";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "../../../design/dialog";
import { Folder } from "../../../models/Folder";
import { Table, TableBody, TableCell, TableRow } from "../../../design/table";
import EmptyStateView from "../../EmptyStateView";
import CreateFolderButton from "../../CreateFolderButton";

interface Props {
  folder?: Folder["_id"];
  setFolder: React.Dispatch<React.SetStateAction<Folder["_id"] | undefined>>;
}

const ChooseFolderDialog: React.FC<Props> = ({ folder, setFolder }) => {
  const { folders } = useFolder();
  const [isOpen, setIsOpen] = useState(false);
  const [folderStack, setFolderStack] = useState<Folder["_id"][]>([]);

  const currentFolderId = folderStack[folderStack.length - 1];

  const currentFolders = folders.filter((f) => {
    if (!currentFolderId) return !f.folder; // Top-level folders (no parent)
    return f.folder === currentFolderId;
  });

  const currentFolderName = (id: Folder["_id"] | undefined) => {
    if (!id) return "Home";
    return folders.find((f) => f._id === id)?.name || "Unknown";
  };

  const handleFolderClick = (folderId: Folder["_id"]) => {
    setFolderStack((prev) => [...prev, folderId]);
  };

  const handleBreadcrumbClick = (index: number) => {
    setFolderStack((prev) => prev.slice(0, index + 1));
  };

  const handleSelectFolder = (folderId: Folder["_id"] | undefined) => {
    setFolder(folderId);
    setIsOpen(false);
  };

  return (
    <>
      <Button onClick={() => setIsOpen(true)}>
        {folder ? currentFolderName(folder) : "Choose Folder"}
      </Button>
      <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
        <DialogTitle>Choose Folder</DialogTitle>
        <DialogDescription>
          <div style={{ marginBottom: "1rem" }}>
            <span
              style={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={() => {
                setFolderStack([]);
              }}
            >
              Home
            </span>
            {folderStack.map((id, index) => (
              <span key={id}>
                {" / "}
                <span
                  style={{ cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => handleBreadcrumbClick(index)}
                >
                  {currentFolderName(id)}
                </span>
              </span>
            ))}
          </div>
        </DialogDescription>
        <DialogBody>
          {currentFolders.length > 0 && (
            <Table>
              <TableBody>
                {currentFolders.map((f) => (
                  <TableRow key={f._id}>
                    <TableCell onClick={() => handleFolderClick(f._id)}>
                      📂 {f.name}
                    </TableCell>
                    <TableCell align="right">
                      <Button onClick={() => handleSelectFolder(f._id)}>
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {currentFolders.length === 0 && (
            <EmptyStateView
              title="No folders found"
              message="This folder is empty."
              action={<CreateFolderButton folderId={currentFolderId} />}
            />
          )}
        </DialogBody>
        <DialogActions>
          <Button onClick={() => setIsOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChooseFolderDialog;
